import { store } from './../vuex/Store'

/**
 * Helper class to assist in constructing and sending both
 * toast messages, as well as desktop notifications.
 *
 * @export
 * 
 * @class MessageHelper
 */
export default new class MessageHelper {

  /**
   * Disperses the changes that have taken place to a server
   * via the Desktop Notification channel as well as the 
   * Browser's Toast Notification channel.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} changes
   * 
   * @returns Void 
   */
  emitServerChanges(changes)
  {
    let browserMessages = this.buildBrowserMessages(changes);
    let desktopMessages = this.buildDesktopMessages(changes);

    this.sendBrowserNotifications(browserMessages);
    this.sendDesktopNotifications(desktopMessages);
  }
  
  /**
   * Sends the relevant messages to the Desktop Notification 
   * channel given a set priority.
   * Priority: status, creation, congestion.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} messages 
   * 
   * @returns Void
   */
  sendDesktopNotifications(messages) 
  {
    // Server status messages.
    if (messages.hasOwnProperty('status') && messages.status.length > 0) {
      if (messages.status.length === 1) {
        this.sendDesktopNotification(`${messages.status[0].server} Server Status Update`, {
          body: messages.status[0].message
        });
      } else {
        this.sendDesktopNotification(`Server Status Update`, {
          body: 'Server statuses for your watched servers have changed.'
        });
      }
    }

    // Character creation messages.
    else if (messages.hasOwnProperty('creation') && messages.creation.length > 0) {
      if (messages.creation.length === 1) {
        this.sendDesktopNotification(`${messages.creation[0].server} Character Creation Update`, {
          body: messages.creation[0].message
        });
      } else {
        this.sendDesktopNotification(`Character Creation Update`, {
          body: 'Character creation statuses for your watched severs have changed.'
        });
      }
    } 
    
    // Congestion level messages.
    else if (messages.hasOwnProperty('congestion') && messages.congestion.length > 0) {
      if (messages.congestion.length === 1) {
        this.sendDesktopNotification(`${messages.congestion[0].server} Server Congestion Update`, {
          body: messages.congestion[0].message
        });
      } else {
        this.sendDesktopNotification(`Server Congestion Update`, {
          body: 'Server congestion levels for your watched severs have changed.'
        });
      }
    }
  }

  /**
   * Send a Desktop Notification using the native browser functionality.
   * 
   * @memberof MessageHelper
   * 
   * @param {String} title 
   * @param {Object} data 
   * 
   * @returns Void
   */
  async sendDesktopNotification(title, data) 
  {
    if (! ('Notification' in window) || ! ('serviceWorker' in navigator)) {
      /* eslint-disable-next-line */
      console.warn('XIVStatus: This browser does not support desktop notifications.')
    } 
    else if (Notification.permission === "granted") {
      try {
        let reg = await navigator.serviceWorker.ready

        reg.showNotification(title, data)
      } catch {
        /* eslint-disable-next-line */
        console.error('XIVStatus: There was an error pushing the desktop notification.')
      }
    } 
    else if (Notification.permission !== 'denied') {
      Notification.requestPermission.then(async permission => {
        if (permission === "granted") {
          try {
            let reg = await navigator.serviceWorker.ready
            
            reg.showNotification(title, data)
          } catch {
            /* eslint-disable-next-line */
            console.error('XIVStatus: There was an error pushing the desktop notification.')
          }
        }
      });
    }
  }

  /**
    * Sends the relevant messages to the Browser's Toast Notification 
   * channel given a set priority.
   * 
   * Priority: status, creation, congestion.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} messages 
   * 
   * @returns Void
   */
  sendBrowserNotifications(messages) 
  {
    for (let server in messages) {
      let obj = messages[server];

      // Server status messages.
      if (obj.hasOwnProperty('status') && obj.status != null) {
        this.sendBrowserNotification({
          title: `${server} Update`,
          body: obj.status.message,
          timeout: 15000,
          sound: 'notification'
        });
      }

      // Character creation messages.
      if (obj.hasOwnProperty('creation') && obj.creation != null) {
        this.sendBrowserNotification({
          title: `${server} Update`,
          body: obj.creation.message,
          timeout: 15000,
          sound: 'notification'
        });
      }

      // Congestion level messages.
      if (obj.hasOwnProperty('congestion') && obj.congestion != null) {
        this.sendBrowserNotification({
          title: `${server} Update`,
          body: obj.congestion.message,
          timeout: 15000,
          sound: 'notification'
        });
      }
    }
  }

  /**
   * Sends a notification to the Toast Queue stored in the Vuex state.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} notification 
   * 
   * @returns Void
   */
  sendBrowserNotification(notification)
  {
    store.commit('addNotification', notification);
  }

  /**
   * Builds a message object that is grouped by arrays of a specific server, followed by type.
   * 
   * Applicable types: status, creation, and congestion.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} diff 
   * 
   * @returns Object
   */
  buildBrowserMessages(diff) 
  {
    let messages = {};

    for (let serverName in diff) {
      let obj = diff[serverName];

      for (let key in obj) {
        if (!messages.hasOwnProperty(serverName)) {
          messages[serverName] = {};
        }

        if (obj[key] !== null) {
          messages[serverName][key] = messages[serverName].hasOwnProperty(key) ? messages[serverName][key] : [];

          messages[serverName][key] = {
            'type': key,
            'server': serverName,
            'message': this.createMessageBody(serverName, key, obj[key])
          };
        }
      }
    }

    return messages;
  }

  /**
   * Builds a message object that is grouped by arrays of a specific type.
   * 
   * Types being: status, creation, and congestion.
   * 
   * @memberof MessageHelper
   * 
   * @param {Object} diff 
   * 
   * @returns Object
   */
  buildDesktopMessages(diff) 
  {
    let messages = {};

    for (let serverName in diff) {
      let obj = diff[serverName];

      for (let key in obj) {
        if (obj[key] !== null) {  
          messages[key] = messages.hasOwnProperty(key) ? messages[key] : [];

          messages[key].push({
            'type': key,
            'server': serverName,
            'message': this.createMessageBody(serverName, key, obj[key])
          });
        }
      }
    }

    return messages;
  }

  /**
   * Creates the body of the notification that will be displayed.
   * 
   * @memberof MessageHelper
   * 
   * @param {String} serverName 
   * @param {String} key 
   * @param {String} value 
   * 
   * @returns String
   */
  createMessageBody(serverName, key, value) 
  {
    // There will be multiple different values that will change for a sever.
    // Of these, we are concerned with Server Status, Congestion Level, and Character Creation.

    if (key === 'status') {
      if (value === 'Maintenance') {
        return `${serverName} is now undergoing maintenance.`;
      } else {
        return `${serverName} is now ${value.toLowerCase()}`;
      }
    }

    if (key === 'congestion' && value !== null) {
      return `The congestion level for ${serverName} is now ${value.toLowerCase()}.`;
    }

    if (key === 'creation' && value !== null) {
      return `Character creation on ${serverName} is now ${value.toLowerCase()}`
    }

    return 'Error: No message was found.';
  }
}